import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  RefreshCw, ArrowUpRight, ArrowDownRight, Flame, Star, ChevronRight
} from 'lucide-react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROP_TYPES = ['All', 'Home Runs', 'Hits', 'Total Bases', 'Stolen Bases'];

const HotProps = () => {
  const navigate = useNavigate();
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterConfidence, setFilterConfidence] = useState('all');
  const [filterPropType, setFilterPropType] = useState('All');

  const fetchProps = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/props/featured`);
      const healthyProps = (response.data.suggestions || []).filter(
        p => p.injury_status === 'Healthy' || p.injury_status === 'Probable'
      );
      setProps(healthyProps);
    } catch (error) {
      console.error('Error fetching props:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchProps(); }, []);

  const goToPlayer = (prop) => {
    if (prop.sport === 'mlb' && prop.player_id) {
      navigate(`/players?pid=${prop.player_id}&name=${encodeURIComponent(prop.player_name || '')}`);
    }
  };

  const filteredProps = props.filter(prop => {
    if (prop.sport !== 'mlb') return false;
    const matchesConfidence = filterConfidence === 'all' || prop.confidence === filterConfidence;
    const matchesType = filterPropType === 'All' || prop.prop_type === filterPropType;
    return matchesConfidence && matchesType;
  });

  const getConfidenceColor = (confidence) => {
    switch(confidence) {
      case 'high': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getSportEmoji = () => '⚾';

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Flame className="h-8 w-8 text-orange-500" />
                Today's Hot Props
              </h1>
              <p className="text-muted-foreground mt-1">
                Home Run, Hits, Total Bases & Stolen Bases picks for healthy, active players
              </p>
            </div>
            <Button 
              onClick={fetchProps}
              variant="outline"
              className="border-[#1e3a5f] text-white hover:bg-[#112240]"
              disabled={refreshing}
              data-testid="refresh-hot-props-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex gap-2">
                <Button
                  variant={filterConfidence === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterConfidence('all')}
                  className={filterConfidence === 'all' 
                    ? 'bg-brand-cyan text-[#0a1628]' 
                    : 'border-[#1e3a5f] text-white hover:bg-[#112240]'
                  }
                  data-testid="confidence-filter-all"
                >
                  All
                </Button>
                <Button
                  variant={filterConfidence === 'high' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterConfidence('high')}
                  className={filterConfidence === 'high' 
                    ? 'bg-green-500 text-white' 
                    : 'border-[#1e3a5f] text-white hover:bg-[#112240]'
                  }
                  data-testid="confidence-filter-high"
                >
                  <Star className="h-4 w-4 mr-1" />
                  High Confidence
                </Button>
                <Button
                  variant={filterConfidence === 'medium' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterConfidence('medium')}
                  className={filterConfidence === 'medium' 
                    ? 'bg-yellow-500 text-[#0a1628]' 
                    : 'border-[#1e3a5f] text-white hover:bg-[#112240]'
                  }
                  data-testid="confidence-filter-medium"
                >
                  Medium
                </Button>
              </div>
            </div>

            {/* Prop Type Filter */}
            <div className="flex flex-wrap gap-2">
              {PROP_TYPES.map(pt => (
                <Button
                  key={pt}
                  variant={filterPropType === pt ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterPropType(pt)}
                  className={filterPropType === pt 
                    ? 'bg-brand-lime text-[#0a1628] hover:bg-brand-lime/90' 
                    : 'border-[#1e3a5f] text-white hover:bg-[#112240]'
                  }
                  data-testid={`prop-type-filter-${pt.toLowerCase().replace(/ /g, '-')}`}
                >
                  {pt}
                </Button>
              ))}
            </div>
          </div>

          {/* Props Count */}
          <div className="mb-6 flex items-center gap-2">
            <Badge variant="outline" className="border-brand-cyan text-brand-cyan">
              {filteredProps.length} Props Available
            </Badge>
            <span className="text-muted-foreground text-sm">
              • Only showing healthy players • Based on last 10 games
            </span>
          </div>

          {/* Props Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-64 skeleton rounded-lg" />
              ))}
            </div>
          ) : filteredProps.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]">
              <CardContent className="p-12 text-center">
                <Flame className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                  No Props Available
                </h3>
                <p className="text-muted-foreground">
                  Adjust your filters to see more props
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProps.map((prop, idx) => (
                <Card 
                  key={prop.id}
                  onClick={() => goToPlayer(prop)}
                  className={`bg-[#0f1e36] border-[#1e3a5f] card-hover stat-card animate-slide-up ${prop.sport === 'mlb' ? 'cursor-pointer' : ''}`}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  data-testid={`hot-prop-card-${idx}`}
                >
                  <CardContent className="p-6">
                    {/* Header with Player Info */}
                    <div className="flex items-start gap-4 mb-4">
                      {prop.headshot && (
                        <img 
                          src={prop.headshot}
                          alt={prop.player_name}
                          className="w-16 h-16 rounded-full bg-[#112240] object-cover border-2 border-brand-cyan"
                          onError={(e) => { e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=200&h=146'; }}
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{getSportEmoji(prop.sport)}</span>
                          <span className={`sport-icon sport-${prop.sport} text-[10px]`}>
                            {prop.team}
                          </span>
                        </div>
                        <h3 className="font-semibold text-white text-lg">{prop.player_name}</h3>
                        <p className="text-sm text-muted-foreground">{prop.prop_type}</p>
                      </div>
                    </div>

                    {/* Recommendation Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                        prop.recommendation === 'under' 
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-green-500/20 text-green-400 border border-green-500/30'
                      }`}>
                        {prop.recommendation === 'under' ? (
                          <ArrowDownRight className="h-4 w-4" />
                        ) : (
                          <ArrowUpRight className="h-4 w-4" />
                        )}
                        {prop.recommendation.toUpperCase()} {prop.line > 0 ? prop.line : ''}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getConfidenceColor(prop.confidence)}`}>
                        {prop.confidence.toUpperCase()}
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-[#0a1628] rounded p-2 text-center">
                        <div className="text-xs text-muted-foreground">Avg L10</div>
                        <div className="text-brand-cyan font-mono font-bold">{prop.avg_last_10}</div>
                      </div>
                      <div className="bg-[#0a1628] rounded p-2 text-center">
                        <div className="text-xs text-muted-foreground">Hit Rate</div>
                        <div className="text-brand-lime font-mono font-bold">{prop.hit_rate}</div>
                      </div>
                      <div className="bg-[#0a1628] rounded p-2 text-center">
                        <div className="text-xs text-muted-foreground">Barrel%</div>
                        <div className="text-white font-mono font-bold">{prop.savant?.barrel_pct ?? '—'}</div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-[#1e3a5f]">
                      <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                        🟢 {prop.injury_status}
                      </span>
                      {prop.sport === 'mlb' && (
                        <span className="text-brand-cyan text-sm font-semibold flex items-center gap-1">
                          View Stats
                          <ChevronRight className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          <Card className="bg-[#0f1e36] border-[#1e3a5f] mt-12">
            <CardContent className="p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-4">
                Want More Insights?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Check today's home run projections for every game, batter, and pitcher in the HR Zone.
              </p>
              <Button 
                onClick={() => navigate('/hr-zone')}
                className="bg-brand-cyan text-[#0a1628] hover:bg-brand-cyan/90 font-bold uppercase tracking-wider"
                data-testid="hrzone-cta-btn"
              >
                Explore HR Zone
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default HotProps;
